// Shared UI bits + helpers for the Import tab.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

export const TERMINAL_BATCH_STATUSES = new Set([
	"completed",
	"failed",
	"reverted",
	"partially_reverted",
]);

// Change 2's primary_reason → a short human-readable phrase for the UI.
export const PRIMARY_REASON_LABELS = {
	flag_column: "flag column",
	most_complete: "most complete data",
	title_seniority: "title seniority",
	file_order: "first row in file",
};

// The CRM **UI** host — deliberately NOT derived from IRIS_API_BASE, which is a
// different host (…iriscrm.com/api/v1). Overridable per environment via
// REACT_APP_IRIS_CRM_BASE (public config, baked into the bundle at build time).
const IRIS_CRM_BASE = String(
	process.env.REACT_APP_IRIS_CRM_BASE || "https://crm.sfprocessing.com"
).replace(/\/+$/, "");

// Deep link to a lead in the Iris CRM UI, e.g.
// https://crm.sfprocessing.com/v2/lead/view/11424 — the real URLs also carry
// `bucket`/`p` list-context params, but those aren't needed to open a lead.
// Returns null for a missing id so callers render plain text instead of a link.
export function irisLeadUrl(leadId) {
	if (leadId === null || leadId === undefined || String(leadId).trim() === "") {
		return null;
	}
	return `${IRIS_CRM_BASE}/v2/lead/view/${encodeURIComponent(String(leadId).trim())}`;
}

// A lead id rendered as a link into Iris CRM (em-dash placeholder when there
// isn't one — failed/pending rows never render an empty link). Reverted and
// externally-deleted leads still link out: a 404 in Iris is informative.
export function IrisLeadLink({ leadId, placeholder = "—", children }) {
	const url = irisLeadUrl(leadId);
	if (!url) return <>{placeholder}</>;
	return (
		<a
			className="irisLeadLink"
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			title={`Open lead ${leadId} in Iris CRM`}>
			{children ?? String(leadId)}
		</a>
	);
}

const STATUS_COLORS = {
	pending: { bg: "#edf2f7", fg: "#4a5568", border: "#cbd5e0" },
	running: { bg: "#ebf8ff", fg: "#2b6cb0", border: "#90cdf4" },
	completed: { bg: "#f0fff4", fg: "#276749", border: "#9ae6b4" },
	failed: { bg: "#fff5f5", fg: "#c53030", border: "#feb2b2" },
	reverting: { bg: "#fffaf0", fg: "#b7791f", border: "#fbd38d" },
	partially_reverted: { bg: "#fffaf0", fg: "#b7791f", border: "#fbd38d" },
	reverted: { bg: "#faf5ff", fg: "#6b46c1", border: "#d6bcfa" },
	imported: { bg: "#f0fff4", fg: "#276749", border: "#9ae6b4" },
	skipped: { bg: "#edf2f7", fg: "#4a5568", border: "#cbd5e0" },
	revert_conflict: { bg: "#fffaf0", fg: "#b7791f", border: "#fbd38d" },
	revert_skipped: { bg: "#edf2f7", fg: "#4a5568", border: "#cbd5e0" },
	deleted_externally: { bg: "#edf2f7", fg: "#718096", border: "#cbd5e0" },
};

// Amber "finished, some leads failed" look for batches that mostly worked.
STATUS_COLORS.attention = STATUS_COLORS.reverting;

// `tone` picks the colours (defaults to the status); `label` replaces the text.
export function StatusChip({ status, tone, label }) {
	const key = String(status || "unknown");
	const c = STATUS_COLORS[tone || key] || STATUS_COLORS.pending;
	return (
		<span
			className="statusChip"
			style={{
				backgroundColor: c.bg,
				color: c.fg,
				border: `1px solid ${c.border}`,
			}}>
			{label || key.replace(/_/g, " ")}
		</span>
	);
}

export function ProgressBar({ value, max }) {
	const pct =
		max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
	return (
		<div className="progressOuter" title={`${value} / ${max}`}>
			<div className="progressInner" style={{ width: `${pct}%` }} />
			<span className="progressText">
				{max > 0 ? `${value} / ${max} (${pct}%)` : "waiting…"}
			</span>
		</div>
	);
}

// Ref that flips to false on unmount — guard async setState with it.
export function useMountedRef() {
	const ref = useRef(true);
	useEffect(() => {
		ref.current = true;
		return () => {
			ref.current = false;
		};
	}, []);
	return ref;
}

// Poll GET /import/batches/{id} until the batch reaches a terminal status.
// Bump refreshKey to re-arm polling after an action (e.g. starting a revert).
export function useBatchPolling(
	api,
	batchId,
	{ intervalMs = 2000, enabled = true, refreshKey = 0 } = {}
) {
	const [batch, setBatch] = useState(null);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!batchId || !enabled) return undefined;
		let cancelled = false;
		let timer = null;

		// Drop data from a previously polled batch (keep it when merely
		// re-arming the same batch via refreshKey).
		setBatch((prev) =>
			prev && String(prev.id) === String(batchId) ? prev : null
		);

		const tick = async () => {
			try {
				const data = await api.getBatch(batchId);
				if (cancelled) return;
				// GET /import/batches/{id} returns {batch, leads} — flatten so
				// consumers read batch.status/imported/... directly.
				const detail = data?.batch
					? { ...data.batch, leads: data.leads ?? [] }
					: data;
				setBatch(detail);
				setError(null);
				if (!TERMINAL_BATCH_STATUSES.has(detail?.status)) {
					timer = setTimeout(tick, intervalMs);
				}
			} catch (err) {
				if (cancelled) return;
				setError(err);
				// Stop on auth errors; retry (slower) on transient ones.
				if (err?.status === 401 || err?.status === 403) return;
				timer = setTimeout(tick, Math.max(intervalMs * 2, 5000));
			}
		};

		tick();
		return () => {
			cancelled = true;
			if (timer) clearTimeout(timer);
		};
	}, [api, batchId, intervalMs, enabled, refreshKey]);

	return { batch, error };
}

// The importApi layer already toasts on 401/403 — don't double-toast those.
export function toastApiError(err, fallbackMessage) {
	if (err && (err.status === 401 || err.status === 403)) return;
	toast.error(
		err && err.message ? `${fallbackMessage}: ${err.message}` : fallbackMessage
	);
}

// Options from GET /import/iris/options may be plain strings or {id, name}.
export function optionId(opt) {
	if (opt && typeof opt === "object") return opt.id ?? opt.value ?? opt.name;
	return opt;
}

export function optionLabel(opt) {
	if (opt && typeof opt === "object") {
		return String(opt.name ?? opt.label ?? opt.id ?? "");
	}
	return String(opt ?? "");
}

// Resolve an option by NAME (case-insensitive) → its id, or null when the list
// has no such option. Used for the named batch defaults ("Brand New" status,
// "ISO" group) so no Iris id is ever hardcoded.
export function optionIdByName(list, name) {
	const target = String(name ?? "").trim().toLowerCase();
	if (!target) return null;
	const found = (list || []).find(
		(o) => optionLabel(o).trim().toLowerCase() === target
	);
	const id = found ? optionId(found) : null;
	return id === null || id === undefined ? null : id;
}

export function optionLabelById(list, id) {
	if (id === null || id === undefined || id === "") return "—";
	const found = (list || []).find(
		(o) => String(optionId(o)) === String(id)
	);
	return found ? optionLabel(found) : String(id);
}

// Batch detail rows key is not pinned by the spec — accept common shapes.
export function batchLeadRows(batch) {
	if (!batch) return [];
	return (
		batch.leads ||
		batch.rows ||
		batch.import_leads ||
		batch.lead_rows ||
		[]
	);
}

export function fmtDateTime(v) {
	if (!v) return "—";
	const d = new Date(v);
	return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

// ---- Never-import columns (scrub metadata / a copy of EXISTING Iris data) --
//
// A scrubbed lead list ("red"/"green" CSV) carries the scrubber's own
// metadata and a copy of the EXISTING Iris data next to the new data — only
// new_* (or plain unprefixed columns in a non-scrub file) can ever be
// imported. Same predicate as the backend (services/import_engine.py
// `is_non_import_column`) and the Leadscrub MCP's `_NON_IMPORT_COLUMN_RE`;
// kept here as a client-side fallback for when the upload response has no
// `hidden_columns` (an older cached response).
const NON_IMPORT_COLUMN_RE =
	/^(existing[_ ]|score$|decision$|bucket[_ ]reason$|uploaded[_ ]index$|best[_ ]db[_ ]index$|domain[_ ]found$|matched[_ ]domain$)|\(do not modify\)/i;

export function isNonImportColumn(col) {
	return NON_IMPORT_COLUMN_RE.test(String(col ?? ""));
}

// {scrubInfo: [...], empty: [...]} — which of an upload's columns must never
// be shown or mapped. Prefers the backend's own `hidden_columns` (already
// computed against the live Iris catalog / whole-file fill counts); falls
// back to the client-side regex plus `empty_columns` when it's missing.
export function hiddenColumns(upload) {
	if (upload?.hidden_columns) {
		return {
			scrubInfo: upload.hidden_columns.scrub_info || [],
			empty: upload.hidden_columns.empty || [],
		};
	}
	const columns = upload?.columns || [];
	const scrubInfo = columns.filter(isNonImportColumn);
	const scrubInfoSet = new Set(scrubInfo);
	const empty = (upload?.empty_columns || []).filter((c) => !scrubInfoSet.has(c));
	return { scrubInfo, empty };
}

// The columns an import screen should actually show/offer for mapping —
// everything else lives behind the "N columns hidden" line.
export function visibleColumns(upload) {
	const hidden = hiddenColumns(upload);
	const hiddenSet = new Set([...hidden.scrubInfo, ...hidden.empty]);
	return (upload?.columns || []).filter((c) => !hiddenSet.has(c));
}

const HIDDEN_NAMES_CAP = 6;

function namesPreview(names) {
	if (names.length <= HIDDEN_NAMES_CAP) return names.join(", ");
	return `${names.slice(0, HIDDEN_NAMES_CAP).join(", ")}, …`;
}

// One line + a <details> with the full lists, so a hidden column is never
// silently lost. Returns null when nothing is hidden.
export function HiddenColumnsNote({ hidden }) {
	const scrubInfo = hidden?.scrubInfo || [];
	const empty = hidden?.empty || [];
	const total = scrubInfo.length + empty.length;
	if (total === 0) return null;
	const parts = [];
	if (scrubInfo.length > 0) parts.push(`scrub info (${namesPreview(scrubInfo)})`);
	if (empty.length > 0) parts.push(`${empty.length} empty in every row`);
	return (
		<details className="importMuted" style={{ fontSize: 13, marginTop: 8 }}>
			<summary style={{ cursor: "pointer" }}>
				{total} column{total === 1 ? "" : "s"} hidden: {parts.join(" and ")}
			</summary>
			<div style={{ marginTop: 6 }}>
				{scrubInfo.length > 0 && (
					<div>Scrub info / existing Iris data (never imported): {scrubInfo.join(", ")}</div>
				)}
				{empty.length > 0 && <div>Empty in every row: {empty.join(", ")}</div>}
			</div>
		</details>
	);
}

const n = (v) => Number(v || 0).toLocaleString();
const plural = (count, word) => `${n(count)} ${word}${Number(count) === 1 ? "" : "s"}`;

// What a batch actually did, in plain words. The raw status can't say
// "mostly worked", and for add-contacts batches "imported" counts leads that
// got contacts — not people — so spell both out.
//   chip:    {tone, label} for StatusChip
//   short:   one line for the history list
//   summary: sentences for the batch detail
//   stats:   [label, value] pairs for the detail stat line
export function batchOutcome(batch) {
	if (!batch) return null;
	const status = batch.status;
	const failed = Number(batch.failed || 0);
	const done = Number(batch.imported || 0);
	const result = batch.params?.result || {};
	const contactMode = batch.params?.mode === "append_contacts";
	const finished = status === "completed" || status === "failed";

	let chip = { tone: status, label: undefined };
	if (finished && result.aborted) {
		chip = { tone: "failed", label: "stopped" };
	} else if (finished && result.unconfirmed_writes > 0) {
		chip = { tone: "failed", label: "needs attention" };
	} else if (finished && done > 0 && failed > 0) {
		chip = { tone: "attention", label: `done · ${n(failed)} failed` };
	} else if (finished && done === 0 && failed > 0) {
		chip = { tone: "failed", label: "failed" };
	} else if (status === "failed" && done > 0) {
		chip = { tone: "attention", label: "partly done" };
	}

	if (contactMode) {
		const added = Number(result.contacts_added || 0);
		const already = Number(result.contacts_skipped || 0);
		const nothing = Number(result.leads_skipped || 0);
		const short =
			`${plural(added, "contact")} added to ${plural(done, "lead")}` +
			(failed ? ` · ${n(failed)} failed` : "");
		const summary = finished
			? [
					`Added ${plural(added, "new contact")} to ${plural(done, "existing lead")}. No new leads were created.`,
					already > 0 &&
						(already === 1
							? "1 person already on their lead was left alone."
							: `${n(already)} people already on their lead were left alone.`),
					nothing > 0 && `${plural(nothing, "lead")} had nobody new to add.`,
					failed > 0 && `${plural(failed, "lead")} ${failed === 1 ? "was" : "were"} not changed — the reason is in the Error column.`,
					result.unconfirmed_writes > 0 &&
						`${plural(result.unconfirmed_writes, "lead")} could not be confirmed — resume the batch to settle ${result.unconfirmed_writes === 1 ? "it" : "them"}.`,
			  ].filter(Boolean)
			: [];
		const stats = [
			["Leads updated", n(done)],
			["Contacts added", n(added)],
			["Already on lead", n(already)],
			["Leads with nobody new", n(nothing)],
			["Leads failed", n(failed)],
			["Leads in file", n(batch.total_leads)],
		];
		return { chip, short, summary, stats, contactMode };
	}

	const short = `${plural(done, "new lead")} created` + (failed ? ` · ${n(failed)} failed` : "");
	const summary = finished
		? [
				`Created ${plural(done, "new lead")}.`,
				failed > 0 && `${plural(failed, "lead")} could not be created — the reason is in the Error column.`,
		  ].filter(Boolean)
		: [];
	const stats = [
		["Leads created", n(done)],
		["Leads failed", n(failed)],
		["Leads in file", n(batch.total_leads)],
	];
	return { chip, short, summary, stats, contactMode };
}

// Per-lead row labels that say what happened to that lead.
export function leadRowChip(rowStatus, contactMode) {
	if (contactMode && rowStatus === "imported") return { label: "contacts added" };
	if (contactMode && rowStatus === "skipped") return { label: "nobody new" };
	if (!contactMode && rowStatus === "imported") return { label: "created" };
	return {};
}
